import { createFileRoute } from '@tanstack/react-router'
import { MarketingPage } from '../components/MarketingPage.js'

export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
})

function PrivacyPage() {
  return (
    <MarketingPage
      eyebrow="Legal"
      title="Privacy policy"
      effectiveDate="17 May 2026"
    >
      <p>
        This Privacy Policy explains how Brightstars (&ldquo;we&rdquo;,
        &ldquo;us&rdquo;, &ldquo;our&rdquo;) collects, uses, shares, and
        protects personal information when you use our software platform,
        website, and related services (the &ldquo;Service&rdquo;).
      </p>
      <p>
        We comply with the Information Technology Act, 2000, the Information
        Technology (Reasonable Security Practices and Procedures and
        Sensitive Personal Data or Information) Rules, 2011, and the Digital
        Personal Data Protection Act, 2023 to the extent applicable.
      </p>

      <h2>1. Who is responsible for your data</h2>
      <p>
        Brightstars is the data controller for information collected directly
        from academy administrators and visitors to brightstars.in. For
        information uploaded by an academy about its parents, students, and
        teachers, the academy is the data controller and Brightstars acts as
        a data processor on the academy&apos;s behalf.
      </p>

      <h2>2. Information we collect</h2>
      <p>We collect the following categories of information:</p>
      <ul>
        <li>
          <strong>Account information:</strong> name, email address, phone
          number, role (admin, teacher, parent), and academy name.
        </li>
        <li>
          <strong>Student information (uploaded by academies):</strong> name,
          date of birth, class, attendance, marks, fee records, and parent
          contact details.
        </li>
        <li>
          <strong>Payment information:</strong> Razorpay payment identifiers,
          transaction status, and bank settlement details. We never store
          full card numbers, CVV, UPI PINs, or netbanking credentials. These
          are handled directly by Razorpay, which is PCI DSS certified.
        </li>
        <li>
          <strong>Communication logs:</strong> records of WhatsApp and
          email messages sent through the Service, including delivery and
          read receipts.
        </li>
        <li>
          <strong>Technical data:</strong> IP address, browser type, device
          identifiers, timestamps, and pages visited, collected through
          server logs and cookies.
        </li>
      </ul>

      <h2>3. How we use your information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Provide, operate, and maintain the Service.</li>
        <li>
          Process subscription payments and parent fee collections through
          Razorpay.
        </li>
        <li>
          Send transactional communications (receipts, attendance alerts,
          test results, fee reminders) on behalf of academies.
        </li>
        <li>
          Respond to support requests and provide customer service.
        </li>
        <li>Detect, prevent, and address fraud, abuse, or security issues.</li>
        <li>
          Comply with legal obligations, including tax, audit, and law
          enforcement requests.
        </li>
        <li>
          Improve the Service through aggregated, de-identified analytics.
        </li>
      </ul>

      <h2>4. How we share your information</h2>
      <p>
        We do not sell personal information. We share information only with:
      </p>
      <ul>
        <li>
          <strong>The associated academy.</strong> Parents and students share
          data with the academy they are enrolled in. Parents linked to
          multiple academies see only their own children&apos;s data.
        </li>
        <li>
          <strong>Service providers</strong> who help us operate the Service
          under written contracts: our payment gateway, cloud hosting and
          database providers, and our messaging providers for WhatsApp and
          email. Your use of those features is also subject to the relevant
          provider&apos;s terms and privacy policies.
        </li>
        <li>
          <strong>Legal and regulatory authorities</strong> when required by
          law, court order, or to protect the rights, property, or safety of
          Brightstars, its users, or the public.
        </li>
        <li>
          <strong>Successors in a business transfer</strong> (merger,
          acquisition, sale of assets), subject to confidentiality
          obligations equivalent to those in this policy.
        </li>
      </ul>

      <h2>5. Data storage and security</h2>
      <p>
        Customer Data is stored with our cloud database provider on managed
        infrastructure. All connections to the Service use HTTPS with TLS 1.2
        or higher. Passwords are hashed using industry standard algorithms.
        Payment data is processed by our payment gateway over PCI DSS compliant
        infrastructure.
      </p>
      <p>
        Access to production systems is restricted to authorised personnel,
        audit-logged, and protected by multi-factor authentication. Row-Level
        Security policies in our database ensure each academy can only see
        its own data.
      </p>

      <h2>6. Data retention</h2>
      <p>
        We retain Customer Data for as long as your account is active. If
        your subscription is cancelled or terminated, we retain Customer Data
        for 30 days to allow export and then delete it from production
        systems. Backups are purged within 90 days. Financial records and
        invoices are retained for at least 8 years to comply with the
        Companies Act, 2013 and tax laws.
      </p>

      <h2>7. Your rights</h2>
      <p>
        Subject to applicable law, you have the right to:
      </p>
      <ul>
        <li>Access the personal information we hold about you.</li>
        <li>Request correction of inaccurate or incomplete information.</li>
        <li>Request deletion of your personal information.</li>
        <li>
          Withdraw consent to processing where processing is based on consent.
        </li>
        <li>Object to or restrict certain processing.</li>
        <li>Receive a copy of your information in a portable format.</li>
        <li>Lodge a complaint with the Data Protection Board of India.</li>
      </ul>
      <p>
        To exercise any of these rights, email{' '}
        <a href="mailto:privacy@brightstars.in">privacy@brightstars.in</a>.
        Parents and students enrolled at an academy should typically raise
        requests with their academy first, as the academy is the controller
        of that information.
      </p>

      <h2>8. Cookies</h2>
      <p>
        We use a small number of essential cookies for authentication, session
        management, and security. We do not use third-party advertising
        cookies. Some pages use anonymous analytics to measure performance.
        No personally identifiable information is shared with analytics
        providers.
      </p>

      <h2>9. Children&apos;s privacy</h2>
      <p>
        Brightstars is intended for use by adults: academy administrators,
        teachers, and parents. Where information about minor students is
        uploaded, it is uploaded by the academy under the lawful basis of its
        contract with the parent or guardian. We process this information
        solely to deliver the Service.
      </p>

      <h2>10. International transfers</h2>
      <p>
        Some of our service providers are headquartered outside India and may
        process limited data outside India. Where this occurs, we rely on
        contractual safeguards and process only the minimum information
        necessary.
      </p>

      <h2>11. Changes to this policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes
        will be communicated by email to the address on file and posted on
        this page with an updated effective date.
      </p>

      <h2>12. Contact</h2>
      <p>
        Questions or complaints about this Privacy Policy can be sent to{' '}
        <a href="mailto:privacy@brightstars.in">privacy@brightstars.in</a>.
        Grievances under the IT Rules, 2021 can be raised with our grievance
        officer at{' '}
        <a href="mailto:grievance@brightstars.in">grievance@brightstars.in</a>.
      </p>
    </MarketingPage>
  )
}
